// Las fotos SUBIDAS de Recrear — UNA fuente para las dos pantallas (`InterfazV2Plantillas`
// y `PlantillasBelleza`), ADR-2026-10-05-recrear-con-todas-subidas-y-subir-imagen.
//
// Recrear ofrece dos puntos de partida: una plantilla del catálogo o una foto propia. La
// foto propia es la colección que ya tenía el modo "Recrear imagen" (`plantillas_propias`,
// `plantillasPropiasApi`): lo que se sube acá aparece allá y al revés. Subir una —o tocar
// una ya subida— lleva al modo con esa foto elegida.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Upload } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { UnsupportedImageFormatError } from '../../../utils/heicConvert';
import CachedImg from '../../../components/CachedImg';
import { empresaRecordada, recordarEmpresa } from '../empresaDeLaSesion';
import {
  fetchPlantillasPropias,
  subirPlantillaPropia,
  type PlantillaPropia,
} from '../plantillasPropiasApi';

/** El id del chip "Subidas". Viaja en `?categoria=` como cualquier otra categoría. */
export const CATEGORIA_SUBIDAS = 'subidas';

/** La ruta del modo "Recrear imagen" (`modesCatalog`, path `replicar-foto`). */
const RUTA_RECREAR_IMAGEN = '/replicar-foto';

/** Lo que Recrear le pasa al modo por `location.state` para que abra con la foto elegida. */
export interface EstadoDeRecrearImagen {
  plantillaId?: string;
}

/**
 * Las subidas de la empresa y las dos acciones de Recrear sobre ellas: subir una nueva y
 * abrir el modo con una elegida. `inputDeArchivo` hay que renderizarlo (una vez) en la
 * pantalla: es el `<input type="file">` oculto que abre `abrirSelector`.
 */
export function useSubidasParaRecrear(brandId: string | null) {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(() => empresaRecordada());
  const [subidas, setSubidas] = useState<PlantillaPropia[]>([]);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      const idCompany = (p as { id_company?: string | null } | null)?.id_company ?? null;
      setCompanyId(idCompany);
      recordarEmpresa(idCompany, auth.user.id);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!companyId) return;
    let cancelado = false;
    fetchPlantillasPropias(companyId)
      .then((lista) => {
        if (!cancelado) setSubidas(lista);
      })
      .catch(() => {
        /* sin subidas la pantalla sigue ofreciendo subir */
      });
    return () => {
      cancelado = true;
    };
  }, [companyId]);

  const abrirSubida = useCallback(
    (p: PlantillaPropia) => {
      const state: EstadoDeRecrearImagen = { plantillaId: p.id };
      navigate(RUTA_RECREAR_IMAGEN, { state });
    },
    [navigate],
  );

  const abrirSelector = useCallback(() => {
    if (!subiendo) inputRef.current?.click();
  }, [subiendo]);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el MISMO archivo
    if (!file || !companyId) return;
    setSubiendo(true);
    setError(null);
    try {
      const nueva = await subirPlantillaPropia({
        file,
        idCompany: companyId,
        brandId,
        cantidadActual: subidas.length,
      });
      setSubidas((prev) => [nueva, ...prev]);
      abrirSubida(nueva);
    } catch (err) {
      console.error('[subidasParaRecrear] subir imagen falló', err);
      setError(
        err instanceof UnsupportedImageFormatError
          ? err.message
          : 'No se pudo subir la imagen. Intentá nuevamente.',
      );
    } finally {
      setSubiendo(false);
    }
  };

  const inputDeArchivo = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      className="hidden"
      onChange={onFile}
    />
  );

  return { subidas, subiendo, error, abrirSelector, abrirSubida, inputDeArchivo };
}

/** La card fija del principio de la grilla: "Subir imagen para recrear". */
export const CardSubirParaRecrear: React.FC<{
  aspectClass: string;
  subiendo: boolean;
  error: string | null;
  onClick: () => void;
}> = ({ aspectClass, subiendo, error, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={subiendo}
    className={`w-full mb-3 sm:mb-4 break-inside-avoid rounded-xl relative flex flex-col items-center justify-center gap-3 px-4 text-center bg-white border border-dashed border-[#02062B33] hover:border-brand-primary text-brand-black active:scale-[0.99] transition-[transform,border-color] ${aspectClass}`}
  >
    <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-[#E9EBF1] text-brand-black">
      {subiendo ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <Upload className="w-5 h-5" />
      )}
    </span>
    <span className="text-[14px] font-medium leading-tight">
      {subiendo ? 'Subiendo…' : 'Subir imagen para recrear'}
    </span>
    {error && !subiendo && (
      <span className="text-[12px] leading-snug text-red-600">{error}</span>
    )}
  </button>
);

/** Una foto subida, con la misma caja que una plantilla de la grilla. */
export const CardSubida: React.FC<{
  subida: PlantillaPropia;
  aspectClass: string;
  onClick: () => void;
}> = ({ subida, aspectClass, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full mb-3 sm:mb-4 break-inside-avoid rounded-xl overflow-hidden bg-neutral-100 relative group active:scale-[0.99] transition-transform block ${aspectClass}`}
  >
    <CachedImg
      src={subida.imageUrl}
      alt={subida.nombre}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ease-out"
    />
  </button>
);
